/**
 * shared/specValidator.ts — the one real spec pre-flight validator.
 *
 * track:specvalidator-drift-fix (spec #43645, backlog #43646). Previously
 * duplicated between src/lib/specValidator.ts (BuildASpecModal.tsx) and
 * server/modules/team/specValidator.ts (the validate_spec MCP tool) with an
 * explicit "keep in lockstep" comment on both — they drifted anyway: three
 * fixes (track:specvalidator-defect-fixes, seq #43430) landed only in the
 * server copy. src/lib/specValidator.ts and server/modules/team/specValidator.ts
 * are now both thin re-export shims of this file — there is exactly one
 * place validation logic can live, so they cannot drift again by
 * construction, not just by a test that catches drift after the fact.
 *
 * Zero Node-only or browser-only imports (same as both files were before
 * this refactor) — pure regex/string logic, safe to import from either a
 * Vite-bundled client module or an esbuild-bundled server module.
 *
 * track:build-a-spec-cross-repo-detection (backlog spec seq #42013) added
 * real CROSS_REPO_UNRESOLVABLE detection via classifyRepo() against a
 * `known_repos` Firestore context key. Fires independently, whenever 2+
 * distinct known repos are matched among the extracted file refs; silent
 * when knownRepos is unavailable. COLLECTION_UNKNOWN remains a stub — see
 * the findings note at seq #42009 for what real detection would require.
 *
 * track:build-a-spec-quality-checks (backlog spec, dev-1 dispatch) added
 * four spec-quality checks (CLARITY_VAGUE_LANGUAGE, SCOPE_CONTROL,
 * SECURITY_PRIVACY, TESTABILITY), all pure text-pattern scans over the
 * combined title+body content. "Measurable threshold nearby" / "observable
 * condition nearby" are both heuristic pattern matches — documented
 * judgment calls, not spec-mandated patterns.
 *
 * track:key-outcome-coverage-check (spec seq #43386) added
 * KEY_OUTCOME_COVERAGE, the first SOFT (non-blocking) check in this file —
 * every check above is hard (any warning fails `passed`). Fires only when
 * the caller supplies `projectKeyOutcomes`; absent/empty input is a silent
 * no-op, so behavior is byte-identical to before for any spec validated
 * without project context.
 *
 * track:specvalidator-defect-fixes (seq #43430) fixed three defects, now
 * ported here from the (previously more complete) server copy: the blast-
 * heading regex accepting a bare "Blast:"/"Blast level:" line (not just a
 * Markdown heading), the vague-word check excluding hyphenated compounds,
 * and the DOCUMENT_ONLY_MARKER escape hatch — generalized below into
 * Override: RULE_CODE — <justification>, with the literal marker string
 * kept as a recognized legacy alias.
 *
 * track:collection-unknown-detection (spec #46024, amended by advisor review
 * #46078) added real COLLECTION_UNDECLARED detection — a pure text scan for
 * Firestore collection references (path-form or bare known-name) missing
 * from a `Collections: a, b, c` declaration line. Soft-only, and a distinct
 * code from the pre-existing COLLECTION_UNKNOWN hard stub above, which is
 * untouched by this track (#46078 Q1: one code, one meaning).
 */

export type BlastLevel = "LOW" | "MEDIUM" | "HIGH" | "CRITICAL" | "UNKNOWN";

export type SpecWarningCode =
  | "MISSING_FILE_REFS"
  | "BLAST_LEVEL_UNCLEAR"
  | "CROSS_REPO_UNRESOLVABLE"
  | "COLLECTION_UNKNOWN"
  | "CLARITY_VAGUE_LANGUAGE"
  | "SCOPE_CONTROL"
  | "SECURITY_PRIVACY"
  | "TESTABILITY"
  | "KEY_OUTCOME_COVERAGE"
  // track:workspace-rule-override-enforcement (seq #44960) — never produced
  // by validateSpec() itself (this file is deliberately workspace-unaware
  // and Firestore-free); appended by the validate_spec MCP handler
  // (server/mcp/teamLog.ts) after a workspace_rule_overrides read this file
  // can't perform. Declared here anyway so it's a real, exhaustiveness-
  // checked SpecWarningCode rather than an ad-hoc string a caller could
  // typo, and so an override line (Override: PEER_REVIEW_OVERRIDE_CONFLICT
  // — ...) can suppress it the same way any other code can.
  | "PEER_REVIEW_OVERRIDE_CONFLICT"
  // track:relay-scheduler-explicit-signals-fix (seq #45339/#45352/#45382) —
  // soft warning: file references resolve to a repo other than the primary
  // ("relay") via classifyRepo(), but no projectId was supplied, so the
  // eventual backlog post can't carry a resolvable project: tag. Produced
  // by validateSpec() itself (unlike PEER_REVIEW_OVERRIDE_CONFLICT above) —
  // knownRepos/classifyRepo are already pure/Firestore-free inputs this
  // function already has, no workspace read needed.
  | "PROJECT_TAG_UNRESOLVED"
  // track:collection-unknown-detection (spec #46024, amended by advisor
  // review #46078) — soft warning: the spec body references a Firestore
  // collection (path-form `name/{id}` or a bare known collection name) that
  // isn't listed in a `Collections: a, b, c` declaration line. Deliberately
  // a separate code from COLLECTION_UNKNOWN above rather than a second
  // condition on it — #46078 Q1: "a code that means 'could not assess' in
  // one branch and 'found an undeclared dependency' in another cannot be
  // reasoned about by any consumer... One code, one meaning." The original
  // spec #46024 named COLLECTION_UNKNOWN in its acceptance criteria; this is
  // the amendment substituting COLLECTION_UNDECLARED throughout.
  | "COLLECTION_UNDECLARED"
  // track:track-write-the-build-spec-for-the-how-relay-works-content-module-agent-tool-and-validator-warning
  // (HRW-1, spec #49640) — soft warning: the spec has no
  // "How Relay Works impact: ..." line. Keeps shared/howRelayWorks.ts from
  // silently going stale as the product changes.
  | "HRW_IMPACT_MISSING"
  // track:rule-hints (spec #51381) — soft warnings for two governance
  // mistakes: wording that tells an agent to wait for a person (Charter
  // §3-D: agents never wait mid-track), and a human member's name where a
  // role belongs. Also run on project background constraints/requirements.
  | "CHARTER_WAIT_CONFLICT"
  | "PERSON_NAMED_IN_RULE";

// Single source of truth for "every real SpecWarningCode value," at both
// runtime and compile time. Override lines reference these by name, and
// since the override regex is built directly from this array (see
// OVERRIDE_LINE_PATTERN below), a typo'd or invalid code can never match
// the regex at all — it fails closed by construction, not via a second
// filter step that could itself have a bug.
//
// The `satisfies` + exhaustiveness check below means this array can't
// silently drift from the SpecWarningCode union in either direction: TS
// errors if a code is added to/removed from the union without this array
// being updated to match.
const SPEC_WARNING_CODES = [
  "MISSING_FILE_REFS",
  "BLAST_LEVEL_UNCLEAR",
  "CROSS_REPO_UNRESOLVABLE",
  "COLLECTION_UNKNOWN",
  "CLARITY_VAGUE_LANGUAGE",
  "SCOPE_CONTROL",
  "SECURITY_PRIVACY",
  "TESTABILITY",
  "KEY_OUTCOME_COVERAGE",
  "PEER_REVIEW_OVERRIDE_CONFLICT",
  "PROJECT_TAG_UNRESOLVED",
  "COLLECTION_UNDECLARED",
  "HRW_IMPACT_MISSING",
  "CHARTER_WAIT_CONFLICT",
  "PERSON_NAMED_IN_RULE",
] as const satisfies readonly SpecWarningCode[];
// Compile-time-only exhaustiveness check — every SpecWarningCode must be
// assignable to (i.e. present in) the array above. Exported (not purely
// local) so tsc's noUnusedLocals doesn't need a fake runtime read to avoid
// flagging it as dead code; nothing needs to actually import this type for
// its job — a conditional type resolving to `never` for a missing code —
// to already have done its work by failing this file's own compilation.
export type AssertSpecWarningCodesExhaustive =
  SpecWarningCode extends (typeof SPEC_WARNING_CODES)[number] ? true : never;
const _exhaustivenessCheck: AssertSpecWarningCodesExhaustive = true;
void _exhaustivenessCheck;

export type SpecWarningSeverity = "hard" | "soft";

export interface SpecWarning {
  code: SpecWarningCode;
  message: string;
  // track:validator-rules (spec #55908) — one-line rewrite hint specific to
  // what fired. Every warning carries one; the static per-rule default lives
  // in SPEC_RULES below.
  fix: string;
  // "hard" fails `passed`; "soft" doesn't. Every check predating
  // KEY_OUTCOME_COVERAGE is hard-only.
  severity: SpecWarningSeverity;
}

export type RecommendedTag = "spec-validated" | "spec-validated:warned" | null;

// track:specvalidator-drift-fix — one applied override, visible on the
// result rather than silent. A legacy-marker match (see
// LEGACY_DOCUMENT_ONLY_MARKER below) produces two entries, one per code it
// maps to, each carrying a synthesized justification naming the legacy
// marker so the read side can tell a legacy-mapped override apart from an
// explicit one if it ever needs to.
export interface AppliedOverride {
  code: SpecWarningCode;
  justification: string;
}

export interface SpecValidationResult {
  fileRefs: string[];
  blastLevel: BlastLevel;
  warnings: SpecWarning[];
  passed: boolean;
  // track:build-a-spec-mandatory-validation (seq #42077) — deterministic
  // signal for which tag a caller should attach to its eventual backlog
  // post. null means "revise and re-validate", not "no tag needed" — see
  // post_message's require-spec-validation enforcement gate, which accepts
  // only "spec-validated" or "spec-validated:warned".
  recommended_tag: RecommendedTag;
  // Empty array when no override was used — never omitted, so a caller can
  // always read it without an existence check.
  overrides_applied: AppliedOverride[];
  // Always populated. track:specvalidator-drift-fix — attributes a passed
  // spec to the exact ruleset version that passed it. Bump when a rule's
  // behavior changes (a new check, a changed threshold/pattern) — not for
  // unrelated refactors like this one, which is why this ships as "1.0.0"
  // rather than something implying prior versions existed.
  ruleset_version: string;
}

export const RULESET_VERSION = "1.1.0";

export interface KnownRepos {
  [repoName: string]: { filePatterns: string[] };
}

// Same pattern as BacklogTab.tsx's FILE_REFERENCE_PATTERN(_GLOBAL) — kept
// byte-for-byte identical so both callers extract the same file refs. Two
// forms: a plain (non-global) one for .test() call sites — a 'g'-flagged
// regex's .test() is stateful (lastIndex) and would intermittently return
// false negatives if reused across calls, so callers doing a presence check
// must use this one, not the global one.
export const FILE_REFERENCE_PATTERN = /`?\b[\w.-]+\/[\w.\-/]+\.[a-zA-Z]{1,10}\b`?/;
export const FILE_REFERENCE_PATTERN_GLOBAL = /`?\b[\w.-]+\/[\w.\-/]+\.[a-zA-Z]{1,10}\b`?/g;

// track:spec-check-false-alarms (spec #56286, item 7) — root-level files have
// no slash, so FILE_REFERENCE_PATTERN above can't see them. Counted only as a
// whole word with a known extension, and never when part of a longer path.
// "js" is left out on purpose: "Node.js" and "Next.js" are product names.
export const ROOT_FILE_PATTERN_GLOBAL =
  /(?<![\w./-])`?[\w-]+(?:\.[\w-]+)*\.(?:md|json|ts|tsx|cjs|mjs|py|ya?ml|toml|sh|css|html|txt)\b(?![\w/-])`?/g;

// track:sprint-fit-tool (spec #57774 item 7) — the template's "Files likely
// touched:" line lists paths, including shared files such as the app shell or
// global styles. Read as written, so a file with an unusual extension counts.
const FILES_LIKELY_TOUCHED_LINE = /^[ \t>*_-]*files likely touched\s*:\*{0,2}\s*(.+)$/gim;

function filesLikelyTouched(content: string): string[] {
  const out: string[] = [];
  for (const m of content.matchAll(FILES_LIKELY_TOUCHED_LINE)) {
    for (const token of m[1].split(/[\s,;]+/)) {
      const t = token.replace(/[`*_()]/g, "").replace(/[.:]+$/, "");
      if (/^[\w./-]*[\w-]\.[A-Za-z0-9]{1,10}$/.test(t) && !t.startsWith("<")) out.push(t);
    }
  }
  return out;
}

export function extractFileRefs(content: string): string[] {
  if (!content) return [];
  const matches = [
    ...(content.match(FILE_REFERENCE_PATTERN_GLOBAL) ?? []),
    ...(content.match(ROOT_FILE_PATTERN_GLOBAL) ?? []),
    ...filesLikelyTouched(content),
  ];
  return Array.from(new Set(matches.map(m => m.replace(/`/g, ""))));
}

// track:specvalidator-defect-fixes (seq #43430, defect 1) — the multi-line
// pattern used to require a literal leading `#{1,6}` right before "Blast",
// which meant it only ever matched a Markdown heading, never a bare
// "Blast:" line, and neither pattern allowed the word "level" between
// "Blast" and the colon. Both gaps are handled once, upstream in
// parseBlastLevel below, by stripping leading heading markers from every
// line before matching (so a heading and a bare line look identical to
// these patterns) and by making "level" an optional word here.
//
// track:blast-line-most-restrictive-parse (backlog #47086) — the capture
// group widened from a single `(LOW|MEDIUM|HIGH|CRITICAL)` token to the
// rest of the line, `(.+)$`. A single-token capture can only ever see
// whatever level word sits immediately after the label, so a hedge like
// "Blast: LOW–MEDIUM, reviewer to confirm" silently resolved to LOW — the
// least restrictive reading, backwards from §3-D/§28-A's "more restrictive
// wins" / "when in doubt, go one level higher." resolveBlastFromContent
// below does the actual level extraction against this wider capture.
export const BLAST_PATTERNS = [
  /^Blast(?:\s+level)?[:\s]*\n+\s*(.+)$/im,
  /\*{0,2}Blast(?:\s+level)?:?\*{0,2}\s+(.+)$/im,
];

// track:blast-line-most-restrictive-parse — mirrors adk-relay's own
// _BLAST_RANK (deterministic_scheduler.py, confirmed numerically identical:
// LOW < MEDIUM < HIGH < CRITICAL) so relay.git's resolution agrees with the
// scheduler's, even though nothing imports across the TS/Python boundary.
// Deferred: applying this same tie-break to deterministic_scheduler.py's
// own independent "Blast:" regex (a second, separate occurrence of this
// bug) is Slice 2 — see backlog #47086's investigation-only finding.
const BLAST_RANK: Record<Exclude<BlastLevel, "UNKNOWN">, number> = { LOW: 0, MEDIUM: 1, HIGH: 2, CRITICAL: 3 };

/** The most restrictive of the given levels, or undefined if the list is empty. */
export function mostRestrictiveBlast(levels: Array<Exclude<BlastLevel, "UNKNOWN">>): Exclude<BlastLevel, "UNKNOWN"> | undefined {
  if (levels.length === 0) return undefined;
  return levels.reduce((worst, level) => (BLAST_RANK[level] > BLAST_RANK[worst] ? level : worst));
}

// Range-style delimiters a hedged blast line joins levels with — a dash
// (hyphen/en-dash/em-dash), a slash, a comma, or the words "or"/"and"/"to".
// Deliberately used only to extend a LEADING run of level tokens (see
// resolveBlastFromContent), not to scan the whole line: "Blast: MEDIUM —
// not HIGH, read-only" must resolve to MEDIUM, not wander into the
// explanatory clause and pick up HIGH — that would gate work that doesn't
// need it and teach people to ignore the resolution.
const LEVEL_TOKEN = "(?:LOW|MEDIUM|HIGH|CRITICAL)";
// A punctuation delimiter (dash/slash/comma) may itself be followed by a
// connective word ("LOW, or MEDIUM"), so that combination is one branch;
// a bare connective word with no punctuation ("LOW or MEDIUM") is the other.
const RANGE_DELIMITER = `(?:\\s*[-–—/,]\\s*(?:(?:or|and|to)\\s+)?|\\s+(?:or|and|to)\\s+)`;
const LEADING_LEVEL_RUN = new RegExp(`^${LEVEL_TOKEN}(?:${RANGE_DELIMITER}${LEVEL_TOKEN})*`, "i");

// track:collection-unknown-detection (spec #46024/#46078) — pure extract of
// the heading-stripping logic parseBlastLevel already had, zero behavior
// change (see the pinning regression test in specValidator.test.ts), so the
// `Collections: a, b, c` declaration line below can recognize the same
// "heading or bare line" shapes without duplicating this logic.
export function stripHeadingMarkers(content: string): string {
  // track:specvalidator-defect-fixes (seq #43430, defect 1) — strips a
  // leading Markdown heading marker (any of `#` through `######`, plus
  // whitespace) from each line first, so "## Blast level: MEDIUM" and
  // "Blast: MEDIUM" hit the exact same patterns below instead of needing
  // one pattern per heading depth.
  return content
    .split("\n")
    .map(line => line.replace(/^\s*#{1,6}\s*/, ""))
    .join("\n");
}

export interface BlastResolution {
  level: BlastLevel;
  /** The raw leading-level-run text that was resolved, e.g. "LOW–MEDIUM" — null when no level was found at all (UNKNOWN). */
  matchedText: string | null;
  /** True when the matched text named more than one distinct level and a tie-break picked the most restrictive. */
  ambiguous: boolean;
}

// The one real implementation both parseBlastLevel and parseBlastLevelDetailed
// share (AC4) — a caller can't diverge from the tie-break by hand-rolling a
// second comparison, because there's only this one to call.
function resolveBlastFromContent(content: string): BlastResolution {
  const stripped = stripHeadingMarkers(content);
  for (const p of BLAST_PATTERNS) {
    const m = stripped.match(p);
    if (!m) continue;
    const runMatch = m[1].match(LEADING_LEVEL_RUN);
    // The label matched but named no level at all (e.g. "reviewer to
    // assess") — a legitimate hedge (§3-D/AC2/AC5), not an error. Try the
    // next pattern rather than treating this as resolved.
    if (!runMatch) continue;
    const levels = Array.from(runMatch[0].matchAll(/LOW|MEDIUM|HIGH|CRITICAL/gi)).map(
      x => x[0].toUpperCase() as Exclude<BlastLevel, "UNKNOWN">,
    );
    const level = mostRestrictiveBlast(levels);
    if (level) return { level, matchedText: runMatch[0], ambiguous: levels.length > 1 };
  }
  return { level: "UNKNOWN", matchedText: null, ambiguous: false };
}

export function parseBlastLevel(content: string): BlastLevel {
  return resolveBlastFromContent(content).level;
}

/** Same resolution as parseBlastLevel, plus what was actually matched and whether a tie-break fired — for surfacing the resolution to a human before they act on it (AC3). */
export function parseBlastLevelDetailed(content: string): BlastResolution {
  return resolveBlastFromContent(content);
}

/** First repo (in registry order) whose filePatterns matches fileRef as a substring, or null. */
export function classifyRepo(fileRef: string, knownRepos: KnownRepos): string | null {
  for (const [repoName, config] of Object.entries(knownRepos)) {
    if (config.filePatterns.some(pattern => fileRef.includes(pattern))) {
      return repoName;
    }
  }
  return null;
}

function joinRepoNames(names: string[]): string {
  if (names.length <= 1) return names.join("");
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\/-]/g, "\\$&");
}

// ─── Override mechanism ─────────────────────────────────────────────────
// track:specvalidator-drift-fix — generalizes the old single-purpose
// DOCUMENT_ONLY_MARKER into a reusable "Override: RULE_CODE — <justification>"
// line an author can use to suppress exactly one named rule's warning for
// this validation call, with the reason recorded (overrides_applied), not
// silent. Modeled directly on DOCUMENT_ONLY_MARKER's own convention: an
// exact, standalone line (not a substring search anywhere in the content),
// so it can't be accidentally triggered by prose that happens to mention
// the word "Override" mid-sentence.
//
// The code alternation is built from SPEC_WARNING_CODES itself, so an
// unknown/invalid code never matches this regex at all — the named rule
// still fires untouched, exactly as if no override were present.
const OVERRIDE_LINE_PATTERN = new RegExp(
  `^Override:\\s*(${SPEC_WARNING_CODES.join("|")})\\s+—\\s+(\\S.*)$`,
);

// track:specvalidator-defect-fixes (seq #43430, defect 3) — legacy alias.
// A document-only spec (Build-A-Spec, Test-A-Feature, research track) has
// no source files to reference at all, so MISSING_FILE_REFS/COLLECTION_UNKNOWN
// hard-block it from ever reaching a spec-validated tag. This exact literal
// line, unchanged from the original DOCUMENT_ONLY_MARKER, is kept as a
// recognized alias mapping to both Override: MISSING_FILE_REFS and
// Override: COLLECTION_UNKNOWN together — every already-filed spec using it
// (e.g. backlog #43426) keeps behaving exactly as before.
const LEGACY_DOCUMENT_ONLY_MARKER = "No source files are changed by this track.";
const LEGACY_MARKER_JUSTIFICATION = `Legacy marker: "${LEGACY_DOCUMENT_ONLY_MARKER}"`;
const LEGACY_MARKER_CODES: readonly SpecWarningCode[] = ["MISSING_FILE_REFS", "COLLECTION_UNKNOWN"];

/**
 * Scans `content` line by line for Override: lines and the legacy marker,
 * returning the set of rule codes to suppress plus the applied-override
 * records to surface on the result. A line that looks like an override but
 * names an invalid code, or has no justification after the em-dash, simply
 * doesn't match — it's silently not an override (the named text, if any
 * real code was intended, still fails validation normally).
 */
// track:workspace-rule-override-enforcement (seq #44960) — exported so the
// validate_spec MCP handler (server/mcp/teamLog.ts) can respect the same
// Override: CODE — justification suppression mechanism for
// PEER_REVIEW_OVERRIDE_CONFLICT, a warning computed outside this pure
// function (it needs a workspace_rule_overrides Firestore read this file
// deliberately doesn't do). Every other warning code gets suppression for
// free by living inside validateSpec() below; this is the one exception.
export function collectOverrides(content: string): { suppressed: Set<SpecWarningCode>; applied: AppliedOverride[] } {
  const suppressed = new Set<SpecWarningCode>();
  const applied: AppliedOverride[] = [];

  for (const rawLine of content.split("\n")) {
    const line = rawLine.trim();
    if (line === LEGACY_DOCUMENT_ONLY_MARKER) {
      for (const code of LEGACY_MARKER_CODES) {
        suppressed.add(code);
        applied.push({ code, justification: LEGACY_MARKER_JUSTIFICATION });
      }
      continue;
    }
    const m = line.match(OVERRIDE_LINE_PATTERN);
    if (!m) continue;
    const code = m[1] as SpecWarningCode;
    const justification = m[2].trim();
    suppressed.add(code);
    applied.push({ code, justification });
  }

  return { suppressed, applied };
}

// ─── CLARITY_VAGUE_LANGUAGE ─────────────────────────────────────────────

const VAGUE_WORDS = [
  "fast", "secure", "simple", "seamless", "real-time", "appropriate",
  "support", "easy", "intuitive", "robust",
];

const MEASURABLE_THRESHOLD_PATTERN =
  /\d+\s*(ms|milliseconds?|seconds?|secs?|minutes?|mins?|hours?|%|percent|mb|gb|kb|requests?\/s|rps|qps|p9\d)\b|<=?\s*\d|>=?\s*\d|\bunder\b|\bwithin\b|\bat least\b|\bno more than\b|\bless than\b|\bfewer than\b|\bexactly\b/i;

function checkClarityVagueLanguage(content: string, warnings: SpecWarning[]): void {
  for (const word of VAGUE_WORDS) {
    // track:specvalidator-defect-fixes (seq #43430, defect 2) — a plain
    // \b...\b boundary treats a hyphen as a delimiter, so it matched inside
    // an unrelated hyphenated compound (e.g. a scheduling term built from one
    // of these words plus "-follow"). Lookaround excluding both word
    // characters AND hyphens on either side keeps the standalone-word match
    // but skips that compound case.
    const re = new RegExp(`(?<![\\w-])${escapeRegExp(word)}(?![\\w-])`, "gi");
    let m: RegExpExecArray | null;
    let flagged = false;
    while (!flagged && (m = re.exec(content)) !== null) {
      const start = Math.max(0, m.index - 100);
      const end = Math.min(content.length, m.index + m[0].length + 100);
      if (!MEASURABLE_THRESHOLD_PATTERN.test(content.slice(start, end))) {
        flagged = true;
      }
    }
    if (flagged) {
      warnings.push({
        code: "CLARITY_VAGUE_LANGUAGE",
        severity: "hard",
        message: `Vague language detected: '${word}' — add an observable threshold or measurable condition so this requirement can be tested and verified.`,
        fix: `Replace '${word}' with a number a test can check, for example "${word} = under 200 ms at the 95th percentile" or "within 2 seconds of the click"; or delete the word.`,
      });
    }
  }
}

// ─── SCOPE_CONTROL ───────────────────────────────────────────────────────

const SCOPE_MIXING_PHRASES = [
  "TBD", "to be determined", "future", "nice to have", "nice-to-have",
  "out of scope but", "later we could", "eventually",
];

// Blanks out code spans (same length, so indexes still line up) so a word
// quoted as code is not read as the spec's own wording.
function maskCodeSpans(content: string): string {
  return content.replace(/`[^`\n]*`/g, m => " ".repeat(m.length));
}

// track:spec-check-false-alarms (spec #56286, item 5) — a phrase glued to a
// hyphen is part of a longer name (the tag "future-feature"), not prose.
// Phrases that are themselves hyphenated ("nice-to-have") still match whole.
function hasScopePhrase(masked: string, phrase: string): boolean {
  const re = new RegExp(`\\b${escapeRegExp(phrase)}\\b`, "gi");
  let m: RegExpExecArray | null;
  while ((m = re.exec(masked)) !== null) {
    if (masked[m.index - 1] === "-" || masked[m.index + m[0].length] === "-") continue;
    return true;
  }
  return false;
}

function checkScopeControl(content: string, warnings: SpecWarning[]): void {
  const masked = maskCodeSpans(content);
  for (const phrase of SCOPE_MIXING_PHRASES) {
    if (hasScopePhrase(masked, phrase)) {
      warnings.push({
        code: "SCOPE_CONTROL",
        severity: "hard",
        message: `Scope-mixing language detected: '${phrase}' — separate launch-blocking requirements from future ideas and unanswered decisions before filing.`,
        fix: `Remove '${phrase}' from the requirements: move the idea under "Out of scope", or decide it now and state the answer.`,
      });
    }
  }
}

// ─── SECURITY_PRIVACY ────────────────────────────────────────────────────

const SENSITIVE_DATA_WORDS = [
  "PII", "PHI", "email", "password", "personal data", "user data",
  "credit card", "payment", "SSN", "health", "medical", "private",
];

// track:spec-checker-false-positives (spec #49129) — a spec with a real
// "Data handling" section covering at least DATA_HANDLING_MIN_TOPICS of the
// canonical topics satisfies this check outright.
const DATA_HANDLING_TOPICS = [
  "purpose", "storage", "access", "deletion", "export", "sharing", "retention",
];
const DATA_HANDLING_MIN_TOPICS = 5;

// track:spec-check-false-alarms (spec #56286, item 3) — "Data handling" or
// "Data access", as a heading of any level, a bold label, a bullet or a
// "Data handling:" line. Heading markers are already stripped by the caller.
const DATA_HANDLING_HEADING = /^\s*(?:[-*]\s+)?\*{0,2}Data (?:handling|access)\b/i;
// A section that says outright "none" declares no personal data is touched.
const DATA_HANDLING_NONE = /^\s*(?:[-*]\s+)?\*{0,2}Data (?:handling|access)\*{0,2}\s*:?\*{0,2}\s*none\b/im;

// Every block that starts with a Data handling or Data access line. The spec
// template puts a "Data access:" line in the header, so the first match is not
// necessarily the real section.
function extractDataHandlingSections(content: string): string[] {
  const lines = content.split("\n");
  const stripped = stripHeadingMarkers(content).split("\n");
  const sections: string[] = [];
  for (let start = 0; start < lines.length; start++) {
    if (!DATA_HANDLING_HEADING.test(stripped[start])) continue;
    const out = [lines[start]];
    for (let i = start + 1; i < lines.length && !/^\s*#{1,6}\s/.test(lines[i]); i++) {
      out.push(lines[i]);
    }
    sections.push(out.join("\n"));
  }
  return sections;
}

function hasDataHandlingSection(content: string): boolean {
  return extractDataHandlingSections(content).some(section => {
    if (DATA_HANDLING_NONE.test(stripHeadingMarkers(section))) return true;
    const lower = section.toLowerCase();
    return DATA_HANDLING_TOPICS.filter(t => lower.includes(t)).length >= DATA_HANDLING_MIN_TOPICS;
  });
}

// Per-occurrence technical-use exemptions, same windowed-match idea as
// checkClarityVagueLanguage: "private function/method/key/repo" and
// "health check" are ordinary engineering terms, not data handling.
const TECHNICAL_USE_AFTER: Record<string, RegExp> = {
  private: /^\s+(function|method|key|repo)\b/i,
  health: /^\s*check\b/i,
};

// track:spec-check-false-alarms (spec #56286, item 2) — a sensitive word only
// counts when its sentence also does something with data (a data verb) or the
// word is used as a field (`email` field, user.email). Bare mentions don't.
// Not when glued to a path or name ("shared/", "log-level"): the word must stand alone.
const DATA_VERB_PATTERN =
  /(?<![\w./-])(?:collect(?:s|ed|ing)?|stor(?:e|es|ed|ing)|sav(?:e|es|ed|ing)|send(?:s|ing)?|sent|shar(?:e|es|ed|ing)|log(?:s|ged|ging)?|export(?:s|ed|ing)?|retain(?:s|ed|ing)?|display(?:s|ed|ing)?)(?![\w/-])/i;

function sentenceAround(content: string, index: number): string {
  const before = content.slice(0, index).split(/(?<=[.!?])\s+|\n/).pop() ?? "";
  const after = content.slice(index).split(/(?<=[.!?])\s+|\n/)[0] ?? "";
  return before + after;
}

function isFieldLike(content: string, index: number, len: number): boolean {
  const before = content.slice(Math.max(0, index - 40), index);
  const after = content.slice(index + len, index + len + 12);
  return /\w\.$/.test(before) || /^`?\s+(?:field|column|property|attribute)\b/i.test(after);
}

function checkSecurityPrivacy(content: string, warnings: SpecWarning[]): void {
  if (hasDataHandlingSection(content)) return;
  const matched: string[] = [];
  for (const word of SENSITIVE_DATA_WORDS) {
    const re = new RegExp(`\\b${escapeRegExp(word)}s?\\b`, "gi");
    const exempt = TECHNICAL_USE_AFTER[word.toLowerCase()];
    let m: RegExpExecArray | null;
    while ((m = re.exec(content)) !== null) {
      const after = content.slice(m.index + m[0].length);
      if (exempt && exempt.test(after)) continue;
      if (DATA_VERB_PATTERN.test(sentenceAround(content, m.index)) || isFieldLike(content, m.index, m[0].length)) {
        matched.push(word);
        break;
      }
    }
  }
  if (matched.length === 0) return;
  const names = matched.map(w => `'${w}'`).join(", ");
  warnings.push({
    code: "SECURITY_PRIVACY",
    severity: "hard",
    message: `This spec appears to handle sensitive data (${names}) — add a "Data handling" section covering: data collection purpose, storage, access control, deletion/export, third-party sharing, logging, and retention. Use "Override: SECURITY_PRIVACY — <justification>" if this is a false positive.`,
    fix: `Add a "Data handling" section naming purpose, storage, access, deletion, export, sharing and retention for ${names}; or add "Override: SECURITY_PRIVACY — <why no personal data is touched>".`,
  });
}

// ─── TESTABILITY ─────────────────────────────────────────────────────────

const NORMATIVE_WORDS_PATTERN = /\b(must|shall|should)\b/gi;
const OBSERVABLE_CONDITION_PATTERN =
  /\bwhen\b|\bthen\b|\bif\b|\bso that\b|\bin order to\b|verif|assert|\btest|measur|confirm/i;
const TESTABILITY_CAP = 3;

function extractSnippet(content: string, matchIndex: number, matchLen: number): string {
  const start = Math.max(0, matchIndex - 30);
  const end = Math.min(content.length, matchIndex + matchLen + 50);
  let snippet = content.slice(start, end).replace(/\s+/g, " ").trim();
  if (start > 0) snippet = `…${snippet}`;
  if (end < content.length) snippet = `${snippet}…`;
  return snippet;
}

// track:spec-checker-false-positives (spec #49129) — a match bracketed by a
// quotation mark before and after it within the same paragraph is a citation
// of someone else's words, not this spec's own requirement. Deliberately a
// local nearest-quote heuristic (not whole-document quote parity) so one stray
// quote far away can't flip later matches; an unclosed quote counts as
// unquoted, the safer direction for a hard check.
const QUOTE_CHARS = /["\u201C\u201D]/;

function isInQuotedSpan(content: string, index: number, len: number): boolean {
  const localBefore = content.slice(0, index).split(/\n[ \t]*\n/).pop() ?? "";
  const after = content.slice(index + len);
  const paraEnd = after.search(/\n[ \t]*\n/);
  const localAfter = paraEnd === -1 ? after : after.slice(0, paraEnd);
  return QUOTE_CHARS.test(localBefore) && QUOTE_CHARS.test(localAfter);
}

// track:spec-check-false-alarms (spec #56286, item 6) — a "Merge risk:" or
// "Deploy check:" line states a fact for the human, not a requirement, so its
// must/should is not scanned. Blanked in place so indexes still line up.
const FACT_FOR_HUMAN_LINE = /^[ \t]*(?:[-*][ \t]+)?\*{0,2}(?:Merge risk|Deploy check):.*$/gim;

function checkTestability(content: string, warnings: SpecWarning[]): void {
  const scanned = content.replace(FACT_FOR_HUMAN_LINE, m => " ".repeat(m.length));
  const re = new RegExp(NORMATIVE_WORDS_PATTERN);
  let m: RegExpExecArray | null;
  let count = 0;
  while (count < TESTABILITY_CAP && (m = re.exec(scanned)) !== null) {
    if (isInQuotedSpan(content, m.index, m[0].length)) continue;
    const windowStart = Math.max(0, m.index - 200);
    const windowEnd = Math.min(content.length, m.index + m[0].length + 200);
    const window = content.slice(windowStart, windowEnd);
    if (!OBSERVABLE_CONDITION_PATTERN.test(window)) {
      const snippet = extractSnippet(content, m.index, m[0].length);
      warnings.push({
        code: "TESTABILITY",
        severity: "hard",
        message: `Normative statement without acceptance condition: '${snippet}' — add an observable behavior and verifiable outcome for every must/shall/should.`,
        fix: `Rewrite as "When <action>, <observable result>" within the same paragraph, for example "When the user saves, the list shows the new row", and add a test that checks it.`,
      });
      count++;
    }
  }
}

// ─── KEY_OUTCOME_COVERAGE ───────────────────────────────────────────────
// track:key-outcome-coverage-check (spec seq #43386) — soft-only. Fires
// only when the caller supplies `projectKeyOutcomes`; absent/empty is a
// silent no-op. Simple keyword-overlap heuristic, same class of check as
// everything else in this file.

const KEY_OUTCOME_STOPWORDS = new Set([
  "the", "a", "an", "and", "or", "of", "to", "in", "on", "for", "is", "are",
  "that", "this", "with", "by", "as", "be", "it", "from", "at", "so", "will",
  "can", "into", "not", "but", "if", "then", "when", "than",
]);

function significantWords(text: string): Set<string> {
  const matches = text.toLowerCase().match(/[a-z0-9]+/g) ?? [];
  return new Set(matches.filter(w => w.length >= 4 && !KEY_OUTCOME_STOPWORDS.has(w)));
}

function checkKeyOutcomeCoverage(
  content: string,
  warnings: SpecWarning[],
  projectKeyOutcomes: string[] | undefined,
): void {
  if (!projectKeyOutcomes || projectKeyOutcomes.length === 0) return;
  const contentWords = significantWords(content);
  const matched = projectKeyOutcomes.some(outcome =>
    Array.from(significantWords(outcome)).some(w => contentWords.has(w))
  );
  if (!matched) {
    warnings.push({
      code: "KEY_OUTCOME_COVERAGE",
      severity: "soft",
      message:
        `This spec doesn't appear to reference any of the project's declared key outcomes ` +
        `(${projectKeyOutcomes.map(o => `"${o}"`).join(", ")}) — confirm it's in scope, or note ` +
        `which outcome it supports. Soft warning: you can still post and revise later.`,
      fix: 'Add a sentence such as "Supports key outcome: <the outcome, in its own words>" to the Why section, or confirm the work is out of project scope.',
    });
  }
}

// ─── COLLECTION_UNDECLARED ───────────────────────────────────────────────
// track:collection-unknown-detection (spec #46024, amended by advisor review
// #46078) — pure text scan, no Firestore read (AC5). Detects two independent
// forms of a Firestore collection reference in a spec body and unions them
// into one Set before comparing against a `Collections: a, b, c` declaration
// line: path-form (`relay_sprints/{sprintId}`) and a bare known-collection
// name. Soft-only (AC3) — reports, never blocks filing.

// Requires a literal `{placeholder}` segment, deliberately distinct from
// FILE_REFERENCE_PATTERN above (which requires a dot-extension) so the two
// never conflict or double-match the same text.
export const COLLECTION_PATH_PATTERN = /\b([a-z][a-z0-9_]*)\/\{[a-zA-Z0-9_]+\}/;

// Real `.collection("...")` literals grepped across server/, shared/, src/.
// Path-form detection above is unambiguous regardless of word rarity and
// covers every name here (and any not here) in `name/{id}` shape; this array
// is only consulted for *bare*-word matching, where rarity matters — see
// EXCLUDED_FROM_BARE_MATCH below for names deliberately left out of it.
const KNOWN_COLLECTIONS = [
  "active_processes", "aiReviews", "api_keys",
  "charter_acks", "charter_rules", "mcp_audit_log", "relay_a2a_tasks",
  "relay_active_sprint", "relay_agent_watchdog", "relay_config",
  "relay_context", "relay_modal", "relay_modal_queue", "relay_sprints",
  "relay_token_usage", "relay_webhook_log", "relay_workspace_members",
  "relay_workspaces", "roster_meta", "team_log_context", "team_log_messages",
];

// track:collection-unknown-detection — advisor review #46078 Q3: approved as
// the right call, not too conservative. Each of these is a real Firestore
// collection name AND an ordinary English word that shows up constantly in
// spec prose; bare-word matching against them would flood false positives.
// Path-form detection (`name/{id}`) still catches every one of them
// unambiguously, so nothing is genuinely lost by leaving them out of
// bare-word matching. Do NOT move these into KNOWN_COLLECTIONS above — that
// "completes" the allowlist and reintroduces the flood this list prevents.
const EXCLUDED_FROM_BARE_MATCH: Record<string, string> = {
  context: "generic word, appears constantly in ordinary spec prose",
  items: "generic word, appears constantly in ordinary spec prose",
  pending: "generic word/status adjective, appears constantly in ordinary spec prose",
  projects: "generic word, appears constantly in ordinary spec prose",
  sprints: "generic word, common outside Firestore context (e.g. \"this sprint's...\")",
  tracks: "generic word, common outside Firestore context (e.g. \"track:\" tags)",
  users: "generic word, appears constantly in ordinary spec prose",
};
// Runtime guard, not just a comment: fails loudly at import time if a future
// edit ever moves one of these back into KNOWN_COLLECTIONS, instead of
// silently reintroducing the false-positive flood #46078 Q3 excluded them
// to prevent.
for (const name of Object.keys(EXCLUDED_FROM_BARE_MATCH)) {
  if (KNOWN_COLLECTIONS.includes(name)) {
    throw new Error(
      `specValidator: '${name}' is listed in EXCLUDED_FROM_BARE_MATCH but also present in ` +
      `KNOWN_COLLECTIONS — see the comment on EXCLUDED_FROM_BARE_MATCH before removing it from there.`,
    );
  }
}

// track:collection-unknown-detection (spec #46024/#46078, Q2) — v1
// convention for declaring the collections a spec touches, same idiom as
// the existing `Blast: LEVEL` line. Recognized under a heading or bare via
// stripHeadingMarkers, same as parseBlastLevel. A bulleted "## Dependencies"
// section is a reasonable v2; nothing about this compact line forecloses it.
export const COLLECTIONS_DECLARATION_PATTERN = /^\*{0,2}Collections?:\*{0,2}\s*(.+)$/im;

function parseDeclaredCollections(content: string): Set<string> {
  const stripped = stripHeadingMarkers(content);
  const m = stripped.match(COLLECTIONS_DECLARATION_PATTERN);
  if (!m) return new Set();
  // Parentheticals after a name ("relay_sprints (read only, ...)") are notes,
  // not names; drop them before splitting on commas.
  return new Set(
    m[1]
      .replace(/\([^)]*\)/g, "")
      .split(",")
      .map(s => s.trim())
      .filter(Boolean),
  );
}

// track:spec-check-false-alarms (spec #56286, item 4) — a REST route is not a
// collection: `repos/{owner}/{repo}` (two placeholders in a row; Firestore
// paths alternate collection and document), anything after `gh api` or an HTTP
// verb, or a path that starts with a slash.
function isApiPath(content: string, index: number, matched: string): boolean {
  const rest = content.slice(index + matched.length);
  if (/^\/\{[a-zA-Z0-9_]+\}/.test(rest)) return true;
  const lineStart = content.lastIndexOf("\n", index - 1) + 1;
  const before = content.slice(lineStart, index);
  if (/\b(?:gh api|GET|POST|PUT|PATCH|DELETE)\s+\S*$/.test(before)) return true;
  return /(?:^|\s)[`"']?\/\S*$/.test(before);
}

function checkCollectionUndeclared(content: string, warnings: SpecWarning[]): void {
  const declared = parseDeclaredCollections(content);
  const found = new Set<string>();

  const pathRe = new RegExp(COLLECTION_PATH_PATTERN.source, "g");
  let m: RegExpExecArray | null;
  while ((m = pathRe.exec(content)) !== null) {
    if (isApiPath(content, m.index, m[0])) continue;
    found.add(m[1]);
  }

  for (const name of KNOWN_COLLECTIONS) {
    const re = new RegExp(`\\b${escapeRegExp(name)}\\b`);
    if (re.test(content)) found.add(name);
  }

  const undeclared = Array.from(found).filter(name => !declared.has(name));
  if (undeclared.length > 0) {
    warnings.push({
      code: "COLLECTION_UNDECLARED",
      severity: "soft",
      message:
        `This spec references Firestore collection(s) ${undeclared.map(c => `'${c}'`).join(", ")} ` +
        `without declaring them — add a "Collections: ${undeclared.join(", ")}" line, or confirm this ` +
        `is intentional. Soft warning: you can still post and revise later.`,
      fix: `Add the line "Collections: ${undeclared.join(", ")}" (or "Collections: none" if nothing is read or written).`,
    });
  }
}

// HRW_IMPACT_MISSING (HRW-1, spec #49640) — soft-only. Accepts "none",
// "stage N / <panel>: <change>", or any other non-empty text after the label.
const HRW_IMPACT_LINE = /^How Relay Works impact:\s*\S/im;

function checkHrwImpact(content: string, warnings: SpecWarning[]): void {
  if (HRW_IMPACT_LINE.test(content)) return;
  warnings.push({
    code: "HRW_IMPACT_MISSING",
    severity: "soft",
    message:
      'Add a "How Relay Works impact: none | stage N / <panel>: <change>" line. Soft warning: you can still save.',
    fix: 'Add one line, for example "How Relay Works impact: none" or "How Relay Works impact: stage 2 Spec it: add \'<the sentence a human now sees>\'".',
  });
}

// ─── CHARTER_WAIT_CONFLICT / PERSON_NAMED_IN_RULE ───────────────────────
// track:rule-hints (spec #51381). Both soft. Pure: human names come in as a
// parameter (the server reads the roster), never fetched here. AI partner and
// platform names never match because they are never in `humanNames`.

export type RuleHintCode = "CHARTER_WAIT_CONFLICT" | "PERSON_NAMED_IN_RULE";

const WORD = "[\\p{L}\\p{N}_]";

function nameRegExp(name: string): RegExp {
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(?<!${WORD})${escaped}(?!${WORD})`, "iu");
}

function cleanNames(humanNames: string[] | undefined): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of humanNames ?? []) {
    const n = raw.trim();
    if (!n || seen.has(n.toLowerCase())) continue;
    seen.add(n.toLowerCase());
    out.push(n);
  }
  return out;
}

const WAIT_TARGET_WORDS = /\b(?:approval|confirmation|reply|response|go-ahead|go ahead)\b/i;
const WAIT_PATTERN = /\bwait(?:s|ing)?\s+(?:for|until)\b((?:\W+[\w'-]+){0,6})/gi;
const PAUSE_PATTERN = /\bpause(?:s|d)?(?:\W+[\w'-]+){0,3}?\W+until\b/i;

function sentenceTellsAgentToWait(sentence: string, names: RegExp[]): boolean {
  if (/\bstop and ask\b/i.test(sentence)) return true;
  if (PAUSE_PATTERN.test(sentence)) return true;
  for (const m of sentence.matchAll(WAIT_PATTERN)) {
    const tail = m[1] ?? "";
    if (WAIT_TARGET_WORDS.test(tail) || names.some((n) => n.test(tail))) return true;
  }
  return names.some((n) => new RegExp(`\\bask\\s+${n.source}\\s+before\\b`, "iu").test(sentence));
}

/**
 * Scans free text for the two rule-hint mistakes. Skips `Override:` lines so
 * a justification can't trigger the check it suppresses. `humanNames`
 * undefined → PERSON_NAMED_IN_RULE and the name parts of
 * CHARTER_WAIT_CONFLICT don't run.
 */
export function checkRuleHints(text: string, humanNames?: string[]): SpecWarning[] {
  const warnings: SpecWarning[] = [];
  const names = cleanNames(humanNames);
  const nameRes = names.map(nameRegExp);
  const lines = text.split("\n").filter((l) => !/^\s*Override:/i.test(l));
  const scanned = lines.join("\n");

  const seenSentences = new Set<string>();
  for (const sentence of scanned.split(/(?<=[.!?])\s+|\n+/)) {
    const s = sentence.trim();
    if (!s || seenSentences.has(s)) continue;
    if (!sentenceTellsAgentToWait(s, nameRes)) continue;
    seenSentences.add(s);
    warnings.push({
      code: "CHARTER_WAIT_CONFLICT",
      severity: "soft",
      message: `This tells an agent to wait for a person ("${s}"). Under the Charter (§3-D) agents never wait mid-track: they end the track with build-stopped naming what's needed. Reword it, for example: 'end the track with build-stopped naming what's needed'.`,
      fix: "Replace the waiting step with \"end the track with build-stopped naming what's needed, from whom, and what was done so far\".",
    });
  }

  names.forEach((name, i) => {
    if (!nameRes[i].test(scanned)) return;
    warnings.push({
      code: "PERSON_NAMED_IN_RULE",
      severity: "soft",
      message: `Names a person ('${name}'). Rules and specs outlive roster changes; name the role instead (for example 'the workspace lead' or 'a human member').`,
      fix: `Replace '${name}' with the role, for example "the workspace lead" or "a human member".`,
    });
  });
  return warnings;
}

// spec #49660 — an investigation (writing tests, answering a question) changes
// no source files, so it has no file references or Firestore collections to
// assess. Explicit `trackType`, a `track_type: investigation` body line, or
// the word Investigation in the title all count — Sprint Planning already
// exempts investigation tracks the same way.
export type SpecTrackType = "build" | "investigation";

function isInvestigationSpec(title: string, body: string, trackType?: SpecTrackType): boolean {
  if (trackType === "investigation") return true;
  if (/\binvestigation\b/i.test(title)) return true;
  return /^\s*track_type:\s*investigation\s*$/im.test(body);
}

export function validateSpec(
  title: string,
  body: string,
  knownRepos?: KnownRepos,
  // track:key-outcome-coverage-check (seq #43386) — union of keyOutcomes
  // across a selected project's declared features[], or undefined/empty to
  // skip the check. Caller-supplied so this stays pure/sync; both callers
  // resolve project background themselves first.
  projectKeyOutcomes?: string[],
  // track:relay-scheduler-explicit-signals-fix (seq #45339/#45352/#45382) —
  // raw project slug, distinct from projectKeyOutcomes above (which is
  // already-resolved data, not identity). Only used by PROJECT_TAG_UNRESOLVED
  // to know whether *any* project association was supplied at all.
  projectId?: string,
  trackType?: SpecTrackType,
  // track:rule-hints (spec #51381) — display names / email local parts of the
  // workspace's human members, supplied by the caller (this stays pure). When
  // omitted, the name checks don't run.
  humanNames?: string[],
): SpecValidationResult {
  const content = `${title}\n\n${body}`;
  const investigation = isInvestigationSpec(title, body, trackType);
  const fileRefs = extractFileRefs(content);
  const blastLevel = parseBlastLevel(content);
  const warnings: SpecWarning[] = [];
  const { suppressed, applied } = collectOverrides(content);

  if (!investigation && fileRefs.length === 0 && !suppressed.has("MISSING_FILE_REFS")) {
    warnings.push({
      code: "MISSING_FILE_REFS",
      severity: "hard",
      message: "Add at least one file path so blast radius can be computed.",
      fix: 'Name the file paths that change, in backticks, for example `server/mcp/teamLog.ts`; for a no-code track add "track_type: investigation".',
    });
  }
  if (!investigation && fileRefs.length === 0 && !suppressed.has("COLLECTION_UNKNOWN")) {
    warnings.push({
      code: "COLLECTION_UNKNOWN",
      severity: "hard",
      message: "Firestore collection dependencies can't be assessed without file references — add file paths first.",
      fix: "Name the file paths that change; this warning clears with MISSING_FILE_REFS.",
    });
  }

  // track:relay-scheduler-explicit-signals-fix (seq #45339/#45352/#45382) —
  // hoisted out of the CROSS_REPO_UNRESOLVABLE block below so
  // PROJECT_TAG_UNRESOLVED can reuse the same classifyRepo pass
  // independently of whether CROSS_REPO_UNRESOLVABLE itself is suppressed —
  // the two codes have separate suppression states and shouldn't share one
  // computation gated on only one of them.
  let matchedRepos: Set<string> | null = null;
  if (knownRepos) {
    matchedRepos = new Set<string>();
    // track:spec-check-false-alarms (spec #56286, item 1) — when projectId
    // names a known repo, a path that belongs to it counts only for it, so
    // `server/` existing in two repos no longer reads as a cross-repo spec.
    const projectRepo = projectId && knownRepos[projectId] ? projectId : null;
    for (const ref of fileRefs) {
      const repo =
        projectRepo && classifyRepo(ref, { [projectRepo]: knownRepos[projectRepo] })
          ? projectRepo
          : classifyRepo(ref, knownRepos);
      if (repo) matchedRepos.add(repo);
    }
  }

  if (matchedRepos && matchedRepos.size > 1 && !suppressed.has("CROSS_REPO_UNRESOLVABLE")) {
    const repoList = Array.from(matchedRepos).sort();
    warnings.push({
      code: "CROSS_REPO_UNRESOLVABLE",
      severity: "hard",
      message: `This spec references files in ${joinRepoNames(repoList)} — cross-repo changes must be sequenced (schema changes in one repo before dependent code in another) or split into separate tracks. Confirm sequencing or split before filing.`,
      fix: "Split into one spec per repo, or state the order (for example \"Slice 1: adk-relay, then Slice 2: relay\") and add \"Override: CROSS_REPO_UNRESOLVABLE — <sequencing>\".",
    });
  }

  // track:relay-scheduler-explicit-signals-fix (seq #45339/#45352/#45382) —
  // soft, not hard: escalating to a blocking requirement is a real behavior
  // change (specs passing today would start failing), out of scope for this
  // additive-only pass (#45339 finding, adk-relay's _detect_secondary_repo
  // fallback). "relay" is the implicit primary repo — same convention
  // adk-relay's own SECONDARY_REPO_MAP encodes with "relay": None — so this
  // only fires when file references clearly point elsewhere with no
  // projectId to carry that association forward as a project: tag.
  if (matchedRepos && matchedRepos.size >= 1 && !suppressed.has("PROJECT_TAG_UNRESOLVED")) {
    const targetsNonPrimaryRepo = Array.from(matchedRepos).some((r) => r !== "relay");
    if (targetsNonPrimaryRepo && !projectId) {
      const repoList = Array.from(matchedRepos).sort();
      warnings.push({
        code: "PROJECT_TAG_UNRESOLVED",
        severity: "soft",
        message: `This spec's file references point to ${joinRepoNames(repoList)}, but no projectId was supplied. Pass projectId so the eventual backlog post carries a resolvable project: tag — without it, adk-relay's scheduler falls back to guessing the repo from file-path substrings in the spec text at dispatch time.`,
        fix: "Pass projectId to validate_spec and add a project:<name> tag to the backlog post.",
      });
    }
  }

  if (blastLevel === "UNKNOWN" && !suppressed.has("BLAST_LEVEL_UNCLEAR")) {
    warnings.push({
      code: "BLAST_LEVEL_UNCLEAR",
      severity: "hard",
      message: 'Add a "Blast: LOW/MEDIUM/HIGH/CRITICAL" line so dispatch priority can be determined.',
      fix: 'Add a line "Blast: LOW" (or MEDIUM, HIGH, CRITICAL) on its own line, with one level only.',
    });
  }

  if (!suppressed.has("CLARITY_VAGUE_LANGUAGE")) checkClarityVagueLanguage(content, warnings);
  if (!suppressed.has("SCOPE_CONTROL")) checkScopeControl(content, warnings);
  if (!suppressed.has("SECURITY_PRIVACY")) checkSecurityPrivacy(content, warnings);
  if (!suppressed.has("TESTABILITY")) checkTestability(content, warnings);
  if (!suppressed.has("KEY_OUTCOME_COVERAGE")) checkKeyOutcomeCoverage(content, warnings, projectKeyOutcomes);
  if (!suppressed.has("COLLECTION_UNDECLARED")) checkCollectionUndeclared(content, warnings);
  if (!suppressed.has("HRW_IMPACT_MISSING")) checkHrwImpact(content, warnings);
  for (const hint of checkRuleHints(content, humanNames)) {
    if (!suppressed.has(hint.code as RuleHintCode)) warnings.push(hint);
  }

  const hardWarnings = warnings.filter(w => w.severity === "hard");
  const passed = hardWarnings.length === 0;
  const hasSoftWarnings = warnings.some(w => w.severity === "soft");
  // Scope note (seq #42077, updated seq #43386): "passed" is "no hard
  // warnings" rather than "no warnings at all" — soft warnings (currently
  // only KEY_OUTCOME_COVERAGE) don't fail it. recommended_tag: null on any
  // hard failure (revise and re-validate); "spec-validated:warned" when
  // passed but a soft warning is present; "spec-validated" otherwise.
  const recommended_tag: RecommendedTag = !passed
    ? null
    : hasSoftWarnings
      ? "spec-validated:warned"
      : "spec-validated";
  return {
    fileRefs,
    blastLevel,
    warnings,
    passed,
    recommended_tag,
    overrides_applied: applied,
    ruleset_version: RULESET_VERSION,
  };
}

// ─── Rule catalogue + spec template ─────────────────────────────────────
// track:validator-rules (spec #55908) — one definition per rule: whether it
// blocks, what triggers it, and how to satisfy or override it. Typed as a
// Record over SpecWarningCode, so adding a code without a definition fails
// tsc; the test suite also fails any definition with empty fix text. The
// `fix` here is the static default; warnings carry a more specific `fix`
// naming what fired.

export interface SpecRuleDefinition {
  severity: SpecWarningSeverity;
  /** One sentence: what makes this rule fire. */
  trigger: string;
  /** How to satisfy the rule (static default of the per-warning `fix`). */
  fix: string;
}

export const SPEC_RULES: Record<SpecWarningCode, SpecRuleDefinition> = {
  MISSING_FILE_REFS: {
    severity: "hard",
    trigger: "A build spec names no file path (investigations are exempt).",
    fix: 'Name the file paths that change, in backticks; for a no-code track add "track_type: investigation".',
  },
  BLAST_LEVEL_UNCLEAR: {
    severity: "hard",
    trigger: "No line gives a Blast level of LOW, MEDIUM, HIGH or CRITICAL.",
    fix: 'Add a line "Blast: LOW" (or MEDIUM, HIGH, CRITICAL).',
  },
  CROSS_REPO_UNRESOLVABLE: {
    severity: "hard",
    trigger: "File paths resolve to two or more repos in the known_repos registry (a path that belongs to the projectId repo counts only for that repo).",
    fix: 'Split into one spec per repo, or state the order and add "Override: CROSS_REPO_UNRESOLVABLE — <sequencing>".',
  },
  COLLECTION_UNKNOWN: {
    severity: "hard",
    trigger: "A build spec names no file path, so collection dependencies can't be assessed (investigations are exempt).",
    fix: "Name the file paths that change; this clears with MISSING_FILE_REFS.",
  },
  CLARITY_VAGUE_LANGUAGE: {
    severity: "hard",
    trigger: `A vague word (${VAGUE_WORDS.join(", ")}) appears with no measurable threshold within 100 characters.`,
    fix: 'Replace the word with a number a test can check, for example "under 200 ms at the 95th percentile".',
  },
  SCOPE_CONTROL: {
    severity: "hard",
    trigger: `Scope-mixing wording appears (${SCOPE_MIXING_PHRASES.join(", ")}).`,
    fix: 'Move the idea under "Out of scope", or decide it now and state the answer.',
  },
  SECURITY_PRIVACY: {
    severity: "hard",
    trigger: `A sensitive-data word appears (${SENSITIVE_DATA_WORDS.join(", ")}) in a sentence that also collects, stores, saves, sends, shares, logs, exports, retains or displays data (or uses the word as a field, such as user.email), with no "Data handling" section covering at least ${DATA_HANDLING_MIN_TOPICS} of: ${DATA_HANDLING_TOPICS.join(", ")}.`,
    fix: 'Add a "Data handling" section covering those topics, or "Override: SECURITY_PRIVACY — <justification>".',
  },
  TESTABILITY: {
    severity: "hard",
    trigger: "A must/shall/should has no when/then/if/verify/test/measure wording within 200 characters (first 3 reported; quoted text is skipped).",
    fix: 'Rewrite as "When <action>, <observable result>" and add a test that checks it.',
  },
  KEY_OUTCOME_COVERAGE: {
    severity: "soft",
    trigger: "projectId was given and the spec shares no significant word with any of the project's declared key outcomes.",
    fix: "Say which key outcome the work supports, or confirm it is in project scope.",
  },
  PEER_REVIEW_OVERRIDE_CONFLICT: {
    severity: "soft",
    trigger: "Appended by the validate_spec tool, not by validateSpec(); the peer-review hold it checked no longer exists, so it is not currently produced.",
    fix: 'Add "Override: PEER_REVIEW_OVERRIDE_CONFLICT — <justification>" if it ever appears.',
  },
  PROJECT_TAG_UNRESOLVED: {
    severity: "soft",
    trigger: "File paths point to a repo other than the primary (relay) and no projectId was given.",
    fix: "Pass projectId to validate_spec and add a project:<name> tag to the backlog post.",
  },
  COLLECTION_UNDECLARED: {
    severity: "soft",
    trigger: "The body references a Firestore collection (name/{id} form or a known collection name) not listed on a `Collections:` line.",
    fix: 'Add "Collections: a, b" listing them, or "Collections: none".',
  },
  HRW_IMPACT_MISSING: {
    severity: "soft",
    trigger: 'No line starts with "How Relay Works impact:" followed by text.',
    fix: 'Add "How Relay Works impact: none" or "How Relay Works impact: stage N / <panel>: <change>".',
  },
  CHARTER_WAIT_CONFLICT: {
    severity: "soft",
    trigger: "A sentence tells an agent to wait for approval, a reply or a named person, to pause until something, or to stop and ask (Charter §3-D).",
    fix: 'Reword to "end the track with build-stopped naming what\'s needed".',
  },
  PERSON_NAMED_IN_RULE: {
    severity: "soft",
    trigger: "The text names a human workspace member.",
    fix: 'Name the role instead, for example "the workspace lead" or "a human member".',
  },
};

export interface SpecRuleListing extends SpecRuleDefinition {
  code: SpecWarningCode;
  /** "blocks" fails `passed`; "warns" does not. */
  effect: "blocks" | "warns";
  override: string;
}

/** Every rule, generated from SPEC_RULES (so it cannot drift from the checks), in code order. */
export function listSpecRules(): { ruleset_version: string; rules: SpecRuleListing[] } {
  const rules = [...SPEC_WARNING_CODES].sort().map((code): SpecRuleListing => {
    const def = SPEC_RULES[code];
    return {
      code,
      ...def,
      effect: def.severity === "hard" ? "blocks" : "warns",
      override: `Override: ${code} — <justification>`,
    };
  });
  return { ruleset_version: RULESET_VERSION, rules };
}

/**
 * Fill-in spec skeleton. Placeholders are written <like this>; replace every
 * one. An investigation template carries `track_type: investigation` and no
 * Collections write line. projectId pre-fills Project and Repo.
 */
export function buildSpecTemplate(trackType: SpecTrackType = "build", projectId?: string): string {
  const project = projectId?.trim() || "<project slug>";
  const header = [
    "**<Spec title>**",
    "<!-- How Relay Works impact: write 'none' unless the change adds or changes something a person or AI partner uses in Relay (a page, button, tool, workflow, tag or rule). Then name the stage and give the sentence to add. -->",
    "<!-- Changing production data? Follow How do I → Change production data safely. -->",
    "",
    `Project: ${project}`,
    `Repo: ${project}`,
    "Blast: <LOW | MEDIUM | HIGH | CRITICAL>",
    `track_type: ${trackType}`,
    "How Relay Works impact: <none | stage N / <panel>: <the sentence a human now sees>>",
  ];
  if (trackType === "build") {
    header.push("Collections: <comma-separated Firestore collections, or none>");
    header.push("Data access: <what is read or written, or none>");
  } else {
    header.push("Data access: read-only. <what is read>");
  }
  const body =
    trackType === "build"
      ? [
          "## Why",
          "<The problem, who has it, and what happens today. Name the key outcome this supports.>",
          "",
          "Files likely touched: <paths, including shared files such as the app shell or global styles>",
          "",
          "## What changes",
          "1. <Change, naming the file paths in backticks, e.g. `server/mcp/teamLog.ts`>",
          "2. <Next change>",
          "",
          "## Acceptance criteria",
          "1. Test in <test file path>: when <action>, <observable result>.",
          "2. Mutation: <remove or break the key line>, confirm the test in 1 fails, then restore it.",
          "3. Full suite and type check pass.",
          "",
          "## Out of scope",
          "<What this spec does not change.>",
          "",
          "Merge risk: <none | the hard-to-undo change on this branch>",
          "Deploy check: <what a human with production access confirms after deploy, or none>",
        ]
      : [
          "## Why",
          "<The question to answer and who needs the answer.>",
          "",
          "## What to find out",
          "1. <Question, naming the places to look>",
          "",
          "## Acceptance criteria",
          "1. The finding answers <question> with evidence: <file path, post number or command output>, and says what is unknown.",
          "",
          "## Out of scope",
          "No code changes.",
          "",
          "Merge risk: none.",
          "Deploy check: none.",
        ];
  return [...header, "", ...body].join("\n");
}
